import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { LogOut, User, LayoutDashboard, Users, Bell, Mail, Hammer, Euro, FileText, Truck, Package, Megaphone, Calendar as CalendarIcon } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { api } from "../lib/api";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const isStaff = user?.role === "personale" || user?.role === "admin";
  const isWorker = user?.role === "operaio";
  const canJobs = isStaff || isWorker;
  const tabletMode = typeof window !== "undefined" && !!localStorage.getItem("energyworld.device_token");

  useEffect(() => {
    if (!isStaff) { setUnread(0); return; }
    const load = async () => {
      try {
        const r = await api.get("/admin/unread");
        setUnread((r.data.unread_quotes || 0) + (r.data.unread_chats || 0) + (r.data.pending_campaigns || 0));
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [isStaff]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200 safe-top safe-x">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/assets/logo.png" alt="Energy World" className="w-10 h-10 rounded-full object-contain shadow-sm"/>
            <div className="leading-tight">
              <div className="font-extrabold text-lg heading text-slate-900">Energy World</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Impianti Tecnologici</div>
            </div>
          </Link>
          <a
            href="https://www.daikin.it/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center shrink-0"
            data-testid="nav-daikin-link"
          >
            <img
              src="/assets/daikin.png"
              alt="Daikin Comfort Store — Partner ufficiale (apre daikin.it in una nuova scheda)"
              title="Daikin Comfort Store — Partner ufficiale"
              className="h-7 md:h-8 w-auto border-l border-slate-200 pl-3 hover:opacity-80 transition-opacity"
              data-testid="nav-daikin-badge"
            />
          </a>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
          <a href="/#servizi" className="hover:text-blue-800 transition-colors">Servizi</a>
          <a href="/#chi-siamo" className="hover:text-blue-800 transition-colors">Chi siamo</a>
          <a href="/#contatti" className="hover:text-blue-800 transition-colors">Contatti</a>
        </nav>
        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">Accedi</Button>
              <Button onClick={() => navigate("/register")} className="bg-blue-800 hover:bg-blue-900" data-testid="nav-register-btn">Registrati</Button>
            </>
          ) : (
            <>
              {isStaff && unread > 0 && (
                <button onClick={() => navigate("/admin/preventivi")} className="relative p-2 rounded-full hover:bg-slate-100" data-testid="nav-unread-bell">
                  <Bell className="w-5 h-5 text-blue-800 animate-pulse" />
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-slate-300 relative pl-2" data-testid="nav-user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name}
                           referrerPolicy="no-referrer"
                           onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "inline-block"; }}
                           className="w-6 h-6 rounded-full object-cover mr-2 border border-slate-200"
                           data-testid="nav-user-avatar"/>
                    ) : null}
                    <User className={`w-4 h-4 mr-2 ${user.picture ? "hidden" : ""}`}/>
                    {user.name?.split(" ")[0]}
                    {isStaff && <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${user.role === "admin" ? "bg-amber-100 text-amber-900" : "bg-blue-100 text-blue-900"}`}>{user.role}</span>}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard admin
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/preventivi-staff")} data-testid="nav-staff-quotes-link">
                      <FileText className="w-4 h-4 mr-2" /> Preventivi (Staff)
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/furgoni")} data-testid="nav-furgoni-link">
                      <Truck className="w-4 h-4 mr-2" /> Furgoni (Tablet)
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/catalogo-materiali")} data-testid="nav-catalog-link">
                      <Package className="w-4 h-4 mr-2" /> Catalogo materiali
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/offerte")} data-testid="nav-offers-link">
                      <Megaphone className="w-4 h-4 mr-2" /> Offerte pubbliche
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/notifiche")} data-testid="nav-push-link">
                      <Bell className="w-4 h-4 mr-2" /> Notifiche push
                    </DropdownMenuItem>
                  )}
                  {isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/admin/mailing")} data-testid="nav-mailing-link">
                      <Mail className="w-4 h-4 mr-2" /> Mailing list
                    </DropdownMenuItem>
                  )}
                  {canJobs && (
                    <DropdownMenuItem onClick={() => navigate("/lavori")} data-testid="nav-jobs-link">
                      <Hammer className="w-4 h-4 mr-2" /> Lavori & Relazioni
                    </DropdownMenuItem>
                  )}
                  {canJobs && (
                    <DropdownMenuItem onClick={() => navigate("/calendario")} data-testid="nav-calendar-link">
                      <CalendarIcon className="w-4 h-4 mr-2" /> Calendario lavori
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin/tariffe")} data-testid="nav-rates-link">
                      <Euro className="w-4 h-4 mr-2" /> Tariffe orarie
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin/utenti")} data-testid="nav-admin-users-link">
                      <Users className="w-4 h-4 mr-2" /> Gestione utenti
                    </DropdownMenuItem>
                  )}
                  {!isStaff && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-link">
                      <User className="w-4 h-4 mr-2" /> Le mie richieste
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="nav-profile-link">
                    <User className="w-4 h-4 mr-2" /> Profilo
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="nav-change-pw-link">
                    Cambia password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {tabletMode && isWorker && (
                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/tablet/home"); }} data-testid="nav-change-user-btn">
                      <Users className="w-4 h-4 mr-2" /> Cambia utente (tablet)
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={async () => { await logout(); navigate(tabletMode ? "/tablet/home" : "/"); }} data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
